import React, { useEffect, useState } from "react";
import { Check, X, Clock, ShieldCheck, Building2, MapPin, Phone, Mail, ExternalLink, Users, Ban, RotateCcw, BadgeCheck, Send } from "lucide-react";
import { AdminShell } from "./AdminShell";
import { adminApi } from "./adminApi";
import { PROFILE_TYPES } from "../profiles";

const FILTERS = [
  { id: "pending", label: "En attente" },
  { id: "apporteurs", label: "Apporteurs" },
  { id: "approved", label: "Validés" },
  { id: "blocked", label: "Bloqués" },
  { id: "rejected", label: "Refusés" },
  { id: "", label: "Tous" },
];

const isApporteur = (m) => (m.account_type || "").trim().toLowerCase() === "apporteur d'affaires";

const STATUS_BADGE = {
  pending: { label: "En attente", cls: "bg-amber-500/20 text-amber-300", icon: Clock },
  approved: { label: "Validé", cls: "bg-emerald-500/20 text-emerald-300", icon: ShieldCheck },
  rejected: { label: "Refusé", cls: "bg-red-500/20 text-red-300", icon: X },
  blocked: { label: "Bloqué", cls: "bg-zinc-500/25 text-zinc-300", icon: Ban },
};

export default function Affiliations({ embedded }) {
  const [filter, setFilter] = useState("pending");
  const [members, setMembers] = useState(null);
  const [busy, setBusy] = useState(null);
  const [sent, setSent] = useState({});

  const load = () => {
    setMembers(null);
    if (filter === "apporteurs") {
      adminApi.listMembers("").then((all) => {
        const aps = (all || []).filter(isApporteur);
        aps.sort((a, b) => (a.status === "pending" ? -1 : 1) - (b.status === "pending" ? -1 : 1));
        setMembers(aps);
      }).catch(() => setMembers([]));
    } else {
      adminApi.listMembers(filter).then(setMembers).catch(() => setMembers([]));
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const setStatus = async (m, status) => {
    setBusy(m.member_id);
    try { await adminApi.setMemberStatus(m.member_id, status); load(); } catch (_) {}
    setBusy(null);
  };

  const changeType = async (m, account_type) => {
    if (!account_type || account_type === m.account_type) return;
    setBusy(m.member_id);
    try { await adminApi.updateMember(m.member_id, { account_type }); load(); } catch (_) {}
    setBusy(null);
  };

  const toggleVerified = async (m) => {
    setBusy(m.member_id);
    try { await adminApi.updateMember(m.member_id, { verified: !m.verified }); load(); } catch (_) {}
    setBusy(null);
  };

  const sendPresentation = async (m) => {
    setBusy(m.member_id);
    setSent((s) => ({ ...s, [m.member_id]: "" }));
    try {
      const r = await adminApi.sendPresentation(m.member_id, { origin_url: window.location.origin });
      setSent((s) => ({ ...s, [m.member_id]: r?.sent ? "ok" : "fail" }));
    } catch (_) {
      setSent((s) => ({ ...s, [m.member_id]: "fail" }));
    }
    setBusy(null);
  };

  const counts = (members || []).length;

  return (
    <AdminShell embedded={embedded} title="Affiliations & conseillers">
      <div className="flex items-center gap-2 mb-6 flex-wrap" data-testid="affiliation-filters">
        {FILTERS.map((f) => (
          <button key={f.id || "all"} onClick={() => setFilter(f.id)} data-testid={`affil-filter-${f.id || "all"}`} className={`text-xs uppercase tracking-wider px-4 py-2 border transition-colors ${filter === f.id ? "bg-[#C19A6B] text-white border-[#C19A6B]" : "border-white/10 text-zinc-300 hover:border-[#C19A6B]/40"}`}>{f.label}</button>
        ))}
        {members !== null && <span className="text-xs text-zinc-500 ml-2">{counts} conseiller(s)</span>}
      </div>

      {members === null ? (
        <p className="text-zinc-500 text-sm">Chargement…</p>
      ) : members.length === 0 ? (
        <div className="text-center py-16 text-zinc-500 border border-dashed border-white/10" data-testid="affil-empty"><Users className="w-8 h-8 mx-auto mb-3 text-zinc-600" />Aucun conseiller dans cette catégorie.</div>
      ) : (
        <div className="grid gap-4" data-testid="affil-list">
          {members.map((m) => {
            const b = STATUS_BADGE[m.status] || STATUS_BADGE.pending;
            const Ic = b.icon;
            return (
              <div key={m.member_id} data-testid={`affil-row-${m.member_id}`} className="bg-[#111114] border border-white/10 p-5 flex flex-col lg:flex-row lg:items-center gap-4">
                <div className="w-14 h-14 rounded-full overflow-hidden border border-[#C19A6B]/40 bg-white/5 flex items-center justify-center shrink-0">
                  {m.photo ? <img src={m.photo} alt="" className="w-full h-full object-cover" /> : <Building2 className="w-6 h-6 text-[#C19A6B]/50" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-white font-medium">{m.display_name}</h3>
                    {(m.verified || m.is_founder) && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 text-[#C19A6B] border border-[#C19A6B]/40" data-testid={`affil-verified-badge-${m.member_id}`}><BadgeCheck className="w-3 h-3" /> Vérifié</span>}
                    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 ${b.cls}`}><Ic className="w-3 h-3" /> {b.label}</span>
                    {m.account_type && <span className="text-[10px] uppercase tracking-wider text-zinc-500 border border-white/10 px-2 py-0.5">{m.account_type}</span>}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-zinc-400 mt-1.5">
                    {m.company && <span className="flex items-center gap-1"><Building2 className="w-3 h-3" /> {m.company}</span>}
                    {m.city && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {m.city}</span>}
                    {m.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {m.phone}</span>}
                    {m.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {m.email}</span>}
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-[10px] uppercase tracking-wider text-zinc-500">Profil :</span>
                    <select value={PROFILE_TYPES.includes(m.account_type) ? m.account_type : ""} onChange={(e) => changeType(m, e.target.value)} disabled={busy === m.member_id} data-testid={`affil-type-${m.member_id}`} className="bg-[#0d0d10] border border-white/15 rounded-[5px] text-xs text-white px-2 py-1 focus:outline-none focus:border-[#C19A6B] disabled:opacity-50">
                      {!PROFILE_TYPES.includes(m.account_type) && m.account_type && <option value="" className="bg-[#0d0d10]">{m.account_type} (actuel)</option>}
                      {PROFILE_TYPES.map((t) => <option key={t} value={t} className="bg-[#0d0d10]">{t}</option>)}
                    </select>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0 flex-wrap">
                  {m.status === "approved" && <a href={`/${m.slug}`} target="_blank" rel="noopener noreferrer" data-testid={`affil-view-${m.member_id}`} title="Voir la page publique" className="p-2 border border-white/10 text-zinc-300 hover:text-[#C19A6B] hover:border-[#C19A6B]/40 transition-colors"><ExternalLink className="w-4 h-4" /></a>}
                  {m.status === "approved" && <button disabled={busy === m.member_id} onClick={() => toggleVerified(m)} data-testid={`affil-verify-${m.member_id}`} title="Badge Vérifié sur les cartes réseau" className={`inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider px-4 py-2 transition-colors disabled:opacity-50 ${m.verified ? "bg-[#C19A6B] text-white" : "border border-[#C19A6B]/40 text-[#C19A6B] hover:bg-[#C19A6B]/10"}`}><BadgeCheck className="w-4 h-4" /> {m.verified ? "Vérifié" : "Certifier"}</button>}
                  {(m.status === "pending" || m.status === "rejected") && <button disabled={busy === m.member_id} onClick={() => setStatus(m, "approved")} data-testid={`affil-approve-${m.member_id}`} className="inline-flex items-center gap-1.5 bg-emerald-500/90 hover:bg-emerald-500 text-black text-xs font-medium uppercase tracking-wider px-4 py-2 transition-colors disabled:opacity-50"><Check className="w-4 h-4" /> Valider</button>}
                  {m.status === "blocked" && <button disabled={busy === m.member_id} onClick={() => setStatus(m, "approved")} data-testid={`affil-reactivate-${m.member_id}`} className="inline-flex items-center gap-1.5 bg-emerald-500/90 hover:bg-emerald-500 text-black text-xs font-medium uppercase tracking-wider px-4 py-2 transition-colors disabled:opacity-50"><RotateCcw className="w-4 h-4" /> Réactiver</button>}
                  {m.status === "approved" && <button disabled={busy === m.member_id} onClick={() => setStatus(m, "blocked")} data-testid={`affil-block-${m.member_id}`} title="Bloquer ce conseiller (ses biens seront masqués)" className="inline-flex items-center gap-1.5 border border-zinc-400/40 text-zinc-300 hover:bg-zinc-500/10 text-xs font-medium uppercase tracking-wider px-4 py-2 transition-colors disabled:opacity-50"><Ban className="w-4 h-4" /> Bloquer</button>}
                  {m.status === "approved" && <button disabled={busy === m.member_id} onClick={() => sendPresentation(m)} data-testid={`affil-presentation-${m.member_id}`} title="Envoyer l'e-mail de présentation de son site (aperçus desktop/tablette/mobile)" className={`inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider px-4 py-2 transition-colors disabled:opacity-50 ${sent[m.member_id] === "ok" ? "bg-emerald-500/90 text-black" : sent[m.member_id] === "fail" ? "border border-red-400/40 text-red-300" : "border border-[#C19A6B]/40 text-[#C19A6B] hover:bg-[#C19A6B]/10"}`}><Send className="w-4 h-4" /> {busy === m.member_id ? "Envoi…" : sent[m.member_id] === "ok" ? "Envoyé ✓" : sent[m.member_id] === "fail" ? "Échec" : "Présentation"}</button>}
                  {m.status !== "rejected" && m.status !== "blocked" && <button disabled={busy === m.member_id} onClick={() => setStatus(m, "rejected")} data-testid={`affil-reject-${m.member_id}`} className="inline-flex items-center gap-1.5 border border-red-400/40 text-red-300 hover:bg-red-500/10 text-xs font-medium uppercase tracking-wider px-4 py-2 transition-colors disabled:opacity-50"><X className="w-4 h-4" /> Refuser</button>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </AdminShell>
  );
}
